import { useState } from "react";
import { ShieldAlert, AlertTriangle, Loader2, MapPin, FileText } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  DISCLAIMER_TITLE,
  DISCLAIMER_INTRO,
  DISCLAIMER_POINTS,
  DISCLAIMER_FOOTER_LINE,
  DISCLAIMER_VERSION,
  ACK_CHECKBOX_READ_LABEL,
  ACK_CHECKBOX_LICENSED_LABEL,
} from "../lib/disclaimer";
import { toast } from "sonner";

/**
 * Full-screen modal shown to authenticated users until they acknowledge the
 * current Clinical Decision Support disclaimer. Cannot be dismissed without
 * acknowledging — there is no close button.
 */
export default function DisclaimerModal() {
  const { refresh, logout } = useAuth();
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = ack1 && ack2 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/acknowledge-disclaimer", {
        ack_read: true,
        ack_licensed: true,
      });
      await refresh();
      toast.success("Acknowledgment recorded. Welcome to ProsthoAI.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record acknowledgment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="disclaimer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-title"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/85 backdrop-blur-sm"
    >
      <div className="w-full sm:max-w-3xl h-dvh sm:h-auto sm:max-h-[90vh] bg-[var(--pa-bg-2)] border-t border-[var(--pa-gold)]/40 sm:border sm:rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-[var(--pa-border)] shrink-0">
          <div className="flex items-start gap-3">
            <ShieldAlert size={22} className="text-[var(--pa-gold)] mt-1 shrink-0" />
            <div>
              <div className="kicker text-[var(--pa-gold)]">Required acknowledgment</div>
              <h2
                id="disclaimer-title"
                data-testid="disclaimer-title"
                className="font-serif text-xl md:text-2xl text-[var(--pa-text)] leading-snug mt-1"
              >
                {DISCLAIMER_TITLE}
              </h2>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="px-6 py-5 overflow-y-auto flex-1" data-testid="disclaimer-body">
          <p className="text-sm text-[var(--pa-text-muted)] leading-relaxed">{DISCLAIMER_INTRO}</p>
          <ol className="mt-5 space-y-4 list-decimal list-inside text-sm text-[var(--pa-text)]">
            {DISCLAIMER_POINTS.map((p, i) => (
              <li key={i} className="leading-relaxed">
                <strong className="text-[var(--pa-gold)] uppercase tracking-wide text-xs mr-1">
                  {p.title.toUpperCase().replace(/\.$/, "")}.
                </strong>
                <span className="text-[var(--pa-text-muted)]">{p.body}</span>
              </li>
            ))}
          </ol>
          <div className="mt-6 pt-4 border-t border-[var(--pa-border)] text-[11px] text-[var(--pa-text-dim)] flex items-center gap-2">
            <MapPin size={11} className="text-[var(--pa-gold)]" />
            {DISCLAIMER_FOOTER_LINE}
          </div>
        </div>

        {/* Acknowledgment + actions */}
        <div className="px-6 py-5 border-t border-[var(--pa-border)] shrink-0 space-y-3 bg-[var(--pa-surface)]">
          <label className="flex items-start gap-3 cursor-pointer min-h-[44px] py-1" data-testid="ack-row-read">
            <input
              type="checkbox"
              checked={ack1}
              onChange={(e) => setAck1(e.target.checked)}
              data-testid="ack-checkbox-read"
              className="mt-1 w-5 h-5 accent-[var(--pa-gold)] shrink-0"
            />
            <span className="text-sm text-[var(--pa-text)]">{ACK_CHECKBOX_READ_LABEL}</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer min-h-[44px] py-1" data-testid="ack-row-licensed">
            <input
              type="checkbox"
              checked={ack2}
              onChange={(e) => setAck2(e.target.checked)}
              data-testid="ack-checkbox-licensed"
              className="mt-1 w-5 h-5 accent-[var(--pa-gold)] shrink-0"
            />
            <span className="text-sm text-[var(--pa-text)]">{ACK_CHECKBOX_LICENSED_LABEL}</span>
          </label>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center sm:justify-between gap-3 pt-2">
            <button
              onClick={logout}
              data-testid="disclaimer-decline"
              className="order-2 sm:order-1 text-sm text-[var(--pa-text-muted)] hover:text-red-400 underline underline-offset-2 min-h-[44px] inline-flex items-center justify-center sm:justify-start"
            >
              I do not agree — sign me out
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              data-testid="disclaimer-acknowledge"
              className="order-1 sm:order-2 inline-flex items-center justify-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-5 py-3 min-h-[44px] hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Recording…
                </>
              ) : (
                <>
                  <FileText size={14} /> I Acknowledge
                </>
              )}
            </button>
          </div>
          <div className="text-[10px] text-[var(--pa-text-dim)] flex items-center gap-1.5">
            <AlertTriangle size={10} />
            Disclaimer version {DISCLAIMER_VERSION}. Acknowledgment is timestamped and recorded in the HIPAA audit log.
          </div>
        </div>
      </div>
    </div>
  );
}
